//* Import libraries
import { useState, useCallback } from "react";

//* Import react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//* Import API
import { tagAPI } from "./tagAPI";

//* Import Pagination type
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import {
  type TagsDetails,
  type CreateTagInput,
  type UpdateTagInput,
} from "./types/types";

export const useTags = (token: string) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const queryKey = [
    "tags",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      tagAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000,
  });

  const tags = data?.tags || [];
  const fetchedPagination = data?.pagination || pagination;

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const createTagMutation = useMutation({
    mutationFn: (tagData: CreateTagInput) => tagAPI.create(tagData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
    },
  });

  const updateTagMutation = useMutation({
    mutationFn: ({ id, ...tagData }: UpdateTagInput) =>
      tagAPI.update(id, { ...tagData, color: tagData.color || "" }, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
    },
  });

  const deleteTagMutation = useMutation({
    mutationFn: (id: string) => tagAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
    },
  });

  const getTagById = useCallback(
    async (id: string): Promise<TagsDetails | null> => {
      try {
        const TagDetails = await tagAPI.getById(id, token);
        return TagDetails;
      } catch (err) {
        console.error("Error fetching Tag by ID:", err);
        return null;
      }
    },
    [token]
  );

  return {
    tags,
    loading: isLoading || isFetching,
    error: isError ? error?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createTag: createTagMutation.mutate,
    updateTag: updateTagMutation.mutate,
    deleteTag: deleteTagMutation.mutate,
    getTagById,
    changePage,
    isCreating: createTagMutation.isPending,
    isUpdating: updateTagMutation.isPending,
    isDeleting: deleteTagMutation.isPending,
  };
};
